import { updateCreditPlanNode } from './assessmentService';

/**
 * Service for handling right panel updates
 */
export class RightPanelService {
  constructor(planId) {
    this.planId = planId;
  }

  /**
   * Update node notes
   * @param {string} stepId - The step ID
   * @param {string} notes - The notes text
   * @returns {Promise<Object>} - Update result
   */
  async updateNotes(stepId, notes) {
    try {
      const result = await updateCreditPlanNode(this.planId, stepId, { notes });
      return { success: true, data: result };
    } catch (error) {
      console.error('Error updating notes:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update node deadline
   * @param {string} stepId - The step ID
   * @param {string} deadline - The deadline date (ISO string or empty string to remove)
   * @returns {Promise<Object>} - Update result
   */
  async updateDeadline(stepId, deadline) {
    try {
      const result = await updateCreditPlanNode(this.planId, stepId, { deadline });
      return { success: true, data: result };
    } catch (error) {
      console.error('Error updating deadline:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update node status
   * @param {string} stepId - The step ID
   * @param {string} status - The status ('standby', 'in-progress', 'done')
   * @returns {Promise<Object>} - Update result
   */
  async updateStatus(stepId, status) {
    try {
      const result = await updateCreditPlanNode(this.planId, stepId, { status });
      return { success: true, data: result };
    } catch (error) {
      console.error('Error updating status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update node todos
   * @param {string} stepId - The step ID
   * @param {Array} todos - The updated todos array
   * @returns {Promise<Object>} - Update result
   */
  async updateTodos(stepId, todos) {
    try {
      const result = await updateCreditPlanNode(this.planId, stepId, { todos });
      return { success: true, data: result };
    } catch (error) {
      console.error('Error updating todos:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Toggle a specific todo item
   * @param {string} stepId - The step ID
   * @param {string} todoId - The todo ID to toggle
   * @param {Array} currentTodos - The current todos array
   * @returns {Promise<Object>} - Update result
   */
  async toggleTodo(stepId, todoId, currentTodos) {
    
    try {
      const updatedTodos = currentTodos.map(todo => 
        todo.id === todoId ? { ...todo, done: !todo.done } : todo
      );
      
      const result = await updateCreditPlanNode(this.planId, stepId, { todos: updatedTodos });
      
      return { success: true, data: result };
    } catch (error) {
      console.error('Error in RightPanelService.toggleTodo:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add a new todo item
   * @param {string} stepId - The step ID
   * @param {string} todoText - The text for the new todo
   * @param {Array} currentTodos - The current todos array
   * @returns {Promise<Object>} - Add result
   */
  async addTodo(stepId, todoText, currentTodos = []) {
    
    try {
      // Create new todo with unique ID and default done status
      const newTodo = {
        id: `todo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: todoText,
        done: false,
        createdAt: new Date().toISOString()
      };
      
      // Add to existing todos array
      const updatedTodos = [...currentTodos, newTodo];
      
      // Update the node with new todos array
      const result = await updateCreditPlanNode(this.planId, stepId, { todos: updatedTodos });
      
      return { 
        success: true, 
        data: result,
        todo: newTodo,
        updatedTodos: updatedTodos
      };
    } catch (error) {
      console.error('Error in RightPanelService.addTodo:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove a specific todo item
   * @param {string} stepId - The step ID
   * @param {string} todoId - The todo ID to remove
   * @param {Array} currentTodos - The current todos array
   * @returns {Promise<Object>} - Remove result
   */
  async removeTodo(stepId, todoId, currentTodos = []) {
    
    try {
      // Remove the todo from the array
      const updatedTodos = currentTodos.filter(todo => todo.id !== todoId);
      
      // Update the node with the updated todos array
      const result = await updateCreditPlanNode(this.planId, stepId, { todos: updatedTodos });
      
      return { 
        success: true, 
        data: result,
        removedTodoId: todoId,
        updatedTodos: updatedTodos
      };
    } catch (error) {
      console.error('Error in RightPanelService.removeTodo:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update multiple fields at once
   * @param {string} stepId - The step ID
   * @param {Object} updates - Object containing multiple fields to update
   * @returns {Promise<Object>} - Update result
   */
  async updateMultiple(stepId, updates) {
    try {
      const result = await updateCreditPlanNode(this.planId, stepId, updates);
      return { success: true, data: result };
    } catch (error) {
      console.error('Error updating multiple fields:', error);
      return { success: false, error: error.message };
    }
  }
} 